import type { BylineConnectionIcon } from '../utils/getBylineConnectionIcons.ts';

import type { useBylineConnectionsData } from './useBylineConnectionsData.ts';
import { useBylineConnectionsLoadTiming } from './useBylineConnectionsLoadTiming.ts';
import { useBylineConnectionsViewedEvent } from './useBylineConnectionsViewedEvent.ts';
import { useBylineIconCountAnalytics } from './useBylineIconCountAnalytics.ts';
import { useBylineTwgResultEvents } from './useBylineTwgResultEvents.ts';

type BylineConnectionsData = ReturnType<typeof useBylineConnectionsData>;

type BylineConnectionsAnalyticsOptions = {
	contentId: string;
	visibility: {
		hasConnection: boolean;
		hiddenBySsr: boolean;
		isResolving: boolean;
		shouldRenderByline: boolean;
		reservedIconCount: number;
		isSsrLoad: boolean;
		devDocsIcons: readonly BylineConnectionIcon[] | undefined;
	};
	data: {
		outgoing: Pick<BylineConnectionsData['outgoing'], 'links' | 'loading' | 'refreshing' | 'error'>;
		incoming: Pick<BylineConnectionsData['incoming'], 'twgLinks' | 'loading' | 'settled' | 'error'>;
		relatedContent: Pick<BylineConnectionsData['relatedContent'], 'links' | 'loading'>;
		devDocsConnection: BylineConnectionsData['devDocsConnection'];
	};
};

/**
 * Every analytics signal the byline itself owns: the load experience, the `byline viewed` event,
 * the `twgResult` events and the `bylineIconCount settled` event. Returns the reporter to call
 * when a click opens the menu.
 */
export const useBylineConnectionsAnalytics = ({
	contentId,
	visibility,
	data,
}: BylineConnectionsAnalyticsOptions): (() => void) => {
	const { outgoing, incoming, devDocsConnection } = data;
	// An incoming failure fails the load only when nothing outbound can show the byline instead.
	const hasOutboundConnection = outgoing.links.length > 0 || devDocsConnection !== null;
	useBylineConnectionsLoadTiming(
		contentId,
		visibility.isResolving,
		visibility.hasConnection,
		outgoing.error || (!hasOutboundConnection ? incoming.error : null),
	);
	useBylineConnectionsViewedEvent(visibility);
	useBylineIconCountAnalytics({ contentId, visibility, data });
	return useBylineTwgResultEvents({ visibility, data });
};
